import React from 'react';
import type { ComponentEventHandler } from '@archipro-design/aria';
import { useTheme } from '@archipro-design/aria';
import { Caption } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import * as S from './Delivery.styles';
import { useLoaderData } from '@remix-run/react';
import { useVariantShipping } from '../../hook/use-variant-shipping';
import type { Locality } from '~/modules/ecommerce/type/locality';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { AngleDown, ChevronDown } from '@archipro-design/icons';
import type { FreeThresholdData } from '~/modules/ecommerce/component/shipping-popup/DeliverySelect';

// ...on ShoppingCartSummary
type DeliveryData = FreeThresholdData & {
    Region?: {
        ID: number;
        Title: string;
    } | null;
    Locality?: Locality | null;
};

interface DeliveryProps {
    isSample: boolean;
    onRegionClick: ComponentEventHandler<void>;
    hasLeadTime?: boolean;
}

const Delivery = ({
    isSample,
    onRegionClick,
    hasLeadTime = false,
}: DeliveryProps) => {
    const { css } = useStyles();
    const { shoppingCartSummary } = useLoaderData<typeof loader>();
    const { shipping, shippingMethods } = useVariantShipping(isSample);
    const { mobile } = useAppDisplayModeContext();
    const theme = useTheme();

    const cart = shoppingCartSummary as DeliveryData;
    const region = cart.Region?.Title ?? null;
    if (!region) {
        return null;
    }

    const shippingMethod =
        shipping?.type === 'delivery' &&
        shippingMethods.find((m) => m.ID === shipping.ID);
    if (!shippingMethod) {
        return null;
    }

    const cost = shippingMethod?.ShippingCostText ?? '';

    const freeShippingThresholdEnabled =
        cart.FreeShippingThreshold && shippingMethod.FreeThresholdEnabled;

    const quotedSeparately = shippingMethod.QuotedSeparately;

    const displayCost =
        freeShippingThresholdEnabled && cart.FreeShippingThresholdMet ? (
            <>
                (<span className={css(S.MetFreeThreshold)}>{cost}</span> Free)
            </>
        ) : (
            cost
        );

    const deliveryText = shippingMethod.DeliveryEstimateText;

    return shippingMethod ? (
        <>
            <div className={css(S.Delivery)}>
                {shippingMethod.Title} {!quotedSeparately && displayCost} to{' '}
                <u className={css(S.Region)} onClick={onRegionClick}>
                    {region}
                    {!mobile && (
                        <ChevronDown className={css(S.DeliverySelectIcon)} />
                    )}
                    {mobile && (
                        <span className={css(S.Icon)}>
                            <AngleDown />
                        </span>
                    )}
                </u>
            </div>
            {deliveryText && (
                <Caption
                    variant="03"
                    className={css(S.DeliveryEstimate)}
                    variables={{
                        captionColor: theme.siteVariables.colors.grey[300],
                    }}
                >
                    {deliveryText}
                    {hasLeadTime ? ', after lead time' : null}
                </Caption>
            )}
            {freeShippingThresholdEnabled && !cart.FreeShippingThresholdMet && (
                <Caption
                    variant="03"
                    className={css(S.FreeShippingThreshold)}
                    variables={{
                        captionColor: theme.siteVariables.colors.grey[300],
                    }}
                >
                    Free Shipping for orders over{' '}
                    {cart.FreeShippingThreshold?.ShortFormat}
                </Caption>
            )}
        </>
    ) : null;
};

export default Delivery;
